import React from 'react'
import { useIntl } from 'react-intl'
import {
  TableContainer,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Typography,
} from '@mui/material'
import { withStyles } from '@mui/styles'

import { basicTableStyles } from './basic-table.styles'

const defaultKey = (i, row) => i

/**
 * @typedef {object} BasicTableColumn
 * @property {string} key - La clave del campo en los datos que se mostrará en esta columna.
 * @property {string} label - El texto que se mostrará en el encabezado de la columna.
 * @property {'left' | 'center' | 'right'} [align='left'] - La alineación del contenido de la columna.
 * @property {'date' | 'datetime' | 'component' | 'responsable' | 'comboDocumentData' | 'comboData'} [type] - El tipo de dato para aplicar formato o renderizado especial.
 * @property {React.ComponentType} [component] - Componente personalizado para renderizar el valor cuando `type` es `'component'`. Recibe props: `value`, `context` y `row`.
 */

/**
 * @typedef {object} BasicTableProps
 * @property {object} classes - Objeto con los nombres de las clases CSS inyectado por `withStyles`.
 * @property {BasicTableColumn[]} config - Array de configuración de columnas que define qué campos mostrar y cómo formatearlos.
 * @property {object[]} data - Array de objetos con los datos a mostrar en la tabla.
 * @property {function(number, object): string|number} [rowKey] - Función para generar una clave única para cada fila. Recibe el índice y el objeto de la fila.
 * @property {any} [context] - Contexto adicional que se pasa a los componentes personalizados de las columnas.
 * @property {any} [getdata] - Datos adicionales disponibles para el componente (uso interno).
 * @property {'small' | 'medium'} [size='medium'] - El tamaño de las celdas de la tabla.
 * @property {function(object): void} [onClick] - Función callback que se ejecuta cuando se hace clic en una fila. Recibe el objeto de la fila.
 * @property {boolean} [loading] - Si es `true`, las filas se muestran con un estilo de carga y los clics están deshabilitados.
 */

/**
 * Un componente de tabla avanzada con soporte para múltiples tipos de datos, componentes personalizados y filas clicables.
 * 
 * Extiende las capacidades de BasicList con características adicionales como:
 * - Renderizado de componentes personalizados en celdas
 * - Formateo de datos especializados (responsables, combos de documentos, etc.)
 * - Filas interactivas con efectos hover
 * - Soporte para internacionalización de valores de combo
 * - Tamaños de tabla configurables
 * 
 * El componente soporta varios tipos de datos predefinidos y permite la integración de componentes
 * React personalizados para casos de uso más complejos.
 * 
 * @component
 * @example
 * const MyTable = () => {
 *   const [selectedRow, setSelectedRow] = useState(null);
 * 
 *   const StatusComponent = ({ value, row }) => (
 *     <Chip label={value} color={row.active ? 'success' : 'default'} />
 *   );
 * 
 *   const columns = [
 *     { key: 'nombre', label: 'Nombre', align: 'left' },
 *     { key: 'estado', label: 'Estado', type: 'component', component: StatusComponent },
 *     { key: 'fecha_creacion', label: 'Fecha', type: 'datetime' },
 *     { key: 'responsable', label: 'Responsable', type: 'responsable' },
 *   ];
 * 
 *   const tableData = [
 *     { nombre: 'Proyecto A', estado: 'activo', active: true, fecha_creacion: '2024-01-15', responsable: 'Juan Pérez', rol_usuario: 'Admin' },
 *     { nombre: 'Proyecto B', estado: 'inactivo', active: false, fecha_creacion: '2024-02-20', responsable: 'María García', rol_usuario: 'Usuario' },
 *   ];
 * 
 *   return (
 *     <BasicTable
 *       config={columns}
 *       data={tableData}
 *       size="small"
 *       onClick={(row) => setSelectedRow(row)}
 *     />
 *   );
 * };
 * 
 * @param {BasicTableProps} props - Las props del componente BasicTable.
 */
export const BasicTable = withStyles(basicTableStyles)(({
  rowKey = defaultKey,
  classes,
  config,
  data,
  context,
  getdata,
  size = 'medium',
  ...props
}) => {
  const { formatDate, formatTime, formatMessage } = useIntl()

  const getParsedValue = (type, value, Component, context, row) => {
    switch (type) {
      case 'date':
        return (
          <Typography variant="body2" color="textSecondary">
            {formatDate(new Date(value))}
          </Typography>
        )
      case 'datetime':
        return (
          <Typography variant="body2" color="textSecondary">
            {formatDate(new Date(value)) + ' - ' + formatTime(new Date(value))}
          </Typography>
        )
      case 'component':
        return <Component value={value} context={context} row={row} />
      case 'responsable':
        const finalValue = row[value] ? `${row[value]} (${row['rol_usuario']})` : null
        return finalValue
      case 'comboDocumentData':
        return (
          <Typography variant="body2" color="textSecondary">
            {formatMessage({ id: `pages.supervision.edit.documents.${value}` })}
          </Typography>
        )
      case 'comboData':
        return (
          <Typography variant="body2" color="textSecondary">
            {formatMessage({ id: `global.basic_table.${value}` })}
          </Typography>
        )
      default:
        return value
    }
  }
  return (
    <TableContainer>
      <Table className={classes.table} aria-label="simple table" size={size}>
        <TableHead>
          <TableRow>
            {config.map(({ align = 'left', label }, i) => (
              <TableCell key={i} align={align}>
                <Typography variant="subtitle1">{label}</Typography>
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {data.map((row, i) => (
            <TableRow
              key={rowKey(i, row)}
              className={
                props.onClick
                  ? props.loading
                    ? classes.loadingRow
                    : classes.clickableRow
                  : classes.row
              }
              hover={!!props.onClick}
              onClick={() => (props.onClick ? props.onClick(row) : null)}
            >
              {config.map(({ key, align = 'left', type, component }, i) => (
                <TableCell key={i} align={align}>
                  {getParsedValue(type, row[key], component, context, row)}
                </TableCell>
              ))}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  )
})
